import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getUniqueId } from '../../utils';
import { getFile } from '../../services';
import { destroyFilesSession } from './actions';
import { getMalwareScanState, isMalwareScanFinal } from './malwareScan';

/**
 * Hook into the component lifecycle to create a session reference that is
 * automatically returned when the component unmounts.
 *
 * @example
 *
 *   const sessionId = useFilesSession();
 *   dispatch(requestFiles(sessionId, ...));
 *
 */
export const useFilesSession = () => {
  const { current: sessionId } = useRef(getUniqueId());
  const dispatch = useDispatch();

  useEffect(() => () => dispatch(destroyFilesSession(sessionId)), []);

  return sessionId;
};

/**
 * Get the malware scan state of a file. A file that was loaded before its scan finished (for
 * example one attached to a comment) is read again from the file service once, so a finished scan
 * does not stay blocked until the page is reloaded. Pass `isRecheckWanted: false` when the caller
 * has no use for a finished scan, so no request is made.
 *
 * @example
 *
 *   const malwareScanState = useMalwareScanState(file);
 *   const isFileClean = malwareScanState === MALWARE_SCAN_STATE.CLEAN;
 *
 */
export const useMalwareScanState = (file, { isRecheckWanted = true } = {}) => {
  const [latestScan, setLatestScan] = useState({
    fileId: null,
    statusWhenRead: null,
    malwareScanStatus: null,
  });
  const isRecheckNeeded =
    isRecheckWanted && !isMalwareScanFinal(getMalwareScanState(file.malwareScanStatus));

  useEffect(() => {
    let isCancelled = false;

    if (isRecheckNeeded) {
      getFile(file.id)
        .then((latestFile) => {
          if (!isCancelled) {
            setLatestScan({
              fileId: file.id,
              statusWhenRead: file.malwareScanStatus,
              malwareScanStatus: latestFile.malwareScanStatus,
            });
          }
        })
        .catch((error) => console.error('Unable to re-check malware scan status:', error));
    }

    return () => {
      isCancelled = true;
    };
  }, [file.id, file.malwareScanStatus, isRecheckNeeded]);

  // A re-check made for an older status of the file must not hide a newer status from the props.
  const isLatestScanCurrent =
    latestScan.fileId === file.id && latestScan.statusWhenRead === file.malwareScanStatus;

  return getMalwareScanState(
    isRecheckNeeded && isLatestScanCurrent ? latestScan.malwareScanStatus : file.malwareScanStatus
  );
};
